import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  omcActiveOpenDemandCyclesActions,
  omcContractsListActions,
  omcDemandByUuidActions,
  omcDemandCycleByUuidActions,
  omcDemandCyclesActions,
  omcDemandSubmissionsActions,
  omcDeleteDemandDraftActions,
  omcOverduePendingBillsSummaryActions,
  omcRequiredGuaranteeSummaryActions,
  omcProductMarketShareSummaryActions,
  omcCreateStockReportActions,
  omcCurrentWindowDemandDraftActions,
  omcSubmitStockReportActions,
  omcUpdateStockReportActions,
  omcStockReportDetailActions,
  omcCreateTerminalAgreementActions,
  omcSubmitTerminalAgreementActions,
  omcUpdateTerminalAgreementActions,
  omcCertificatePreviewActions,
  omcDemandProjectionActions,
  omcDraftDemandActions,
  omcListActions,
  omcStockReportsActions,
  omcSubmitUsageReportActions,
  omcUpdateUsageReportActions,
  omcTerminalAgreementByUuidActions,
  omcTerminalSameTinActions,
  omcTerminalAgreementsActions,
  omcTransitConsigneesActions,
  omcTransitStocksActions,
  deleteTransitStockActions,
  deleteTransitConsigneeActions,
  deleteOmcDemandCargoProductActions,
  deleteOmcDemandProductUsageActions,
  omcSubmitDemandActions,
  OmcSignContractActions,
  loggedInOmcLastMonthsProjectsActions,
  loadBpsCalendarActions,
} from './omc-management.actions';
import { GraphQlService } from '@shared/services/graphql-service';
import {
  ACTIVE_OPEN_DEMAND_CYCLES,
  CHECK_OMC_TERMINAL_SAME_TIN,
  CREATE_OMC_STOCK_REPORT,
  CURRENT_WINDOW_DEMAND_DRAFT,
  DELETE_OMC_DEMAND_DRAFT,
  GET_OVERDUE_PENDING_BILL_SUMMARY,
  REQURIRED_GUARANTEE_SUMMARY,
  REQUIRED_OMC_PRODUCT_MARKET_SHARE_SUMMARY,
  UPDATE_OMC_STOCK_REPORT,
  GET_OMC_CONTRACTS,
  GET_ALL_OMC_TERMINAL_AGREEMENTS,
  GET_OMC_STOCK_REPORT_DETAIL,
  GET_OMC_CERTIFICATE_PREVIEW,
  GET_OMC_STOCK_REPORT_BY_OMC,
  GET_OMC_TERMINAL_AGREEMENT,
  LIST_OMC_REGISTRATION,
  OMC_DEMAND_BY_UUID,
  OMC_DEMAND_CYCLE_BY_UUID,
  OMC_DEMAND_CYCLES,
  OMC_DEMAND_SUBMISSIONS,
  CREATE_OMC_TERMINAL_AGREEMENT,
  SUBMIT_OMC_TERMINAL_AGREEMENT,
  UPDATE_OMC_TERMINAL_AGREEMENT,
  SAVE_OMC_DEMAND_PROJECTION,
  SAVE_TRANSIT_CONSIGNEES,
  SAVE_TRANSIT_STOCK,
  DELETE_TRANSIT_STOCK,
  DELETE_TRANSIT_CONSIGNEE,
  DELETE_OMC_DEMAND_CARGO_PRODUCT,
  DELETE_OMC_DEMAND_PRODUCT_USAGE,
  SAVE_OMC_DRAFT_DEMAND,
  SUBMIT_OMC_STOCK_REPORT,
  SUBMIT_OMC_USAGE_REPORT,
  UPDATE_OMC_USAGE_REPORT,
  SUBMIT_OMC_DEMAND,
  SIGN_OMC_CONTRACT,
  GET_LOGGED_IN_OMC_LAST_MONTHS_PROJECTS,
  GET_PUBLIC_ATTACHMENTS,
} from '@modules/external-services/omc-management/graphql/omc-management.graphql';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  OmcDemandSubmissionsResponse,
  OmcContractsListResponse,
  OmcRegistrationListResponse,
} from '../response/list-registration.response';
import {
  SaveDemandProjectionResponse,
  SaveDraftDemandResponse,
} from '../response/save-draft-demand.response';
import { SaveTransitConsigneesResponse } from '../response/save-transit-consignee.response';
import { SaveTransitStockResponse } from '../response/save-transit-stock.response';
import { DeleteTransitStockResponse } from '../response/delete-transit-stock.response';
import { DeleteTransitConsigneeResponse } from '../response/delete-transit-consignee.response';
import {
  DeleteOmcDemandCargoProductResponse,
  DeleteOmcDemandProductUsageResponse,
} from '../response/delete-omc-demand-cargo-product.response';
import {
  CheckOmcTerminalSameTinResponse,
  CreateOmcTerminalAgreementResponse,
  GetOmcTerminalAgreementResponse,
  GetOmcTerminalAgreementsResponse,
  SubmitOmcTerminalAgreementResponse,
  UpdateOmcTerminalAgreementResponse,
} from '../response/omc-terminal-agreement.response';
import { SubmitOmcDemandResponse } from '../response/submit-omc-demand.response';
import {
  ActiveOpenDemandCyclesResponse,
  OmcDemandCycleByUuidResponse,
  OmcDemandCyclesResponse,
} from '../response/omc-demand-cycles.response';
import {
  OmcDemandByUuidResponse,
  CurrentOmcDemandDraftResponse,
  DeleteOmcDemandDraftResponse,
  OmcDemandSubmissionsResponse as OmcDemandSubmissionsQueryResponse,
  OverduePendingBillsSummaryResponse,
  RequiredGuaranteeSummaryResponse,
  GetLoggedInOmcLastMonthDemandProjectionResponse,
  RequiredOmcProductMarketShareSummaryResponse,
} from '../response/omc-demand-submissions.response';
import {
  CreateOmcStockReportResponse,
  GetOmcStockReportDetailResponse,
  GetOmcStockReportsByOmcResponse,
  SubmitOmcStockReportResponse,
  SubmitOmcUsageReportResponse,
  UpdateOmcUsageReportResponse,
  UpdateOmcStockReportResponse,
} from '../response/omc-stock-report.response';
import { SignOmcContractResponse } from '../response/sing-omc-contract.response';
import { GetOmcCertificatePreviewResponse } from '../response/omc-certificate-preview.response';

@Injectable({ providedIn: 'root' })
export class OmcManagementEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
  ) { }

  loadOmcList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcListActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcRegistrationListResponse>({
            query: LIST_OMC_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.listOmcRegistration;
              if (!payload) {
                return omcListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }
              return omcListActions.success({
                response: {
                  //success: true,
                  //statusCode: 200,
                  // message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcContractsListActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcContractsListResponse>({
            query: GET_OMC_CONTRACTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.getOmcContracts;
              if (!payload) {
                return omcContractsListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }
              return omcContractsListActions.success({
                response: {
                  data: payload,
                  //success: true,
                  //statusCode: 200,
                  //message: 'Success',
                  //response: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcContractsListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcDemandSubmissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDemandSubmissionsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcDemandSubmissionsQueryResponse>({
            query: OMC_DEMAND_SUBMISSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.omcDemandSubmissions;
              if (!payload) {
                return omcDemandSubmissionsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcDemandSubmissionsActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcDemandSubmissionsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcDemandCycles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDemandCyclesActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcDemandCyclesResponse>({
            query: OMC_DEMAND_CYCLES,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.demandCycles;
              if (!payload) {
                return omcDemandCyclesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcDemandCyclesActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcDemandCyclesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadActiveOpenDemandCycles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcActiveOpenDemandCyclesActions.request),
      switchMap(() =>
        this.graphQlService
          .query<ActiveOpenDemandCyclesResponse>({
            query: ACTIVE_OPEN_DEMAND_CYCLES,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.activeOpenDemandCycles?.data;
              if (!payload) {
                return omcActiveOpenDemandCyclesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.data?.activeOpenDemandCycles?.message ?? 'Failed',
                    data: null,
                  },
                });
              }

              return omcActiveOpenDemandCyclesActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcActiveOpenDemandCyclesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcDemandCycleByUuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDemandCycleByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcDemandCycleByUuidResponse>({
            query: OMC_DEMAND_CYCLE_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.demandCycleByUuid?.data;
              if (!payload) {
                return omcDemandCycleByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.data?.demandCycleByUuid?.message ?? 'Failed',
                    data: null,
                  },
                });
              }

              return omcDemandCycleByUuidActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcDemandCycleByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcDemandByUuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDemandByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcDemandByUuidResponse>({
            query: OMC_DEMAND_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request.uuid,
              includeCycle: request.includeCycle ?? true,
              includeLines: request.includeLines ?? true,
              includeTerminals: request.includeTerminals ?? true,
              includeConsignees: request.includeConsignees ?? true,
              includeTransitStocks: request.includeTransitStocks ?? true,
              includeProjections: request.includeProjections ?? true,
              includeAttachments: request.includeAttachments ?? true,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.omcDemandByUuid?.data;
              if (!payload) {
                return omcDemandByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.data?.omcDemandByUuid?.message ?? 'Failed',
                    data: null,
                  },
                });
              }

              return omcDemandByUuidActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcDemandByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadCurrentWindowDemandDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcCurrentWindowDemandDraftActions.request),
      switchMap(() =>
        this.graphQlService
          .query<CurrentOmcDemandDraftResponse>({
            query: CURRENT_WINDOW_DEMAND_DRAFT,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.currentOmcDemandDraft;
              if (!payload) {
                return omcCurrentWindowDemandDraftActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch current demand draft',
                    data: null,
                  },
                });
              }

              return omcCurrentWindowDemandDraftActions.success({
                response: {
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                omcCurrentWindowDemandDraftActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  saveOmcDraftDemand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDraftDemandActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveDraftDemandResponse>({
            mutation: SAVE_OMC_DRAFT_DEMAND,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.saveOmcDemandDraft;
              if (!payload) {
                return omcDraftDemandActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? '',
                    data: null,
                  },
                });
              }
              return omcDraftDemandActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcDraftDemandActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  saveOmcDemandProjection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDemandProjectionActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveDemandProjectionResponse>({
            mutation: SAVE_OMC_DEMAND_PROJECTION,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.saveOmcDemandProjection;
              if (!payload || payload.status === false) {
                return omcDemandProjectionActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to save demand projection',
                    data: payload?.data ?? null,
                  },
                });
              }
              return omcDemandProjectionActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcDemandProjectionActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  saveTransitConsignees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcTransitConsigneesActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveTransitConsigneesResponse>({
            mutation: SAVE_TRANSIT_CONSIGNEES,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.saveTransitConsignees;
              if (!payload || payload.status === false) {
                return omcTransitConsigneesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ?? response.error?.message ?? 'Failed to save  consignees',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcTransitConsigneesActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcTransitConsigneesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  saveTransitStocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcTransitStocksActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveTransitStockResponse>({
            mutation: SAVE_TRANSIT_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.saveTransitStocks;
              if (!payload || payload.status === false) {
                return omcTransitStocksActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to save transit stocks',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcTransitStocksActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcTransitStocksActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteTransitStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTransitStockActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteTransitStockResponse>({
            mutation: DELETE_TRANSIT_STOCK,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              stockUuid: request.uuid,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.deleteTransitStockInTank;
              if (!payload || payload.status === false) {
                return deleteTransitStockActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to delete transit stock',
                    data: payload?.data ?? null,
                  },
                });
              }

              return deleteTransitStockActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                deleteTransitStockActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteTransitConsignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTransitConsigneeActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteTransitConsigneeResponse>({
            mutation: DELETE_TRANSIT_CONSIGNEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              transitConsigneeUuid: request.uuid,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.deleteTransitConsignee;
              if (!payload || payload.status === false) {
                return deleteTransitConsigneeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to delete transit consignee',
                    data: payload?.data ?? null,
                  },
                });
              }

              return deleteTransitConsigneeActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                deleteTransitConsigneeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitOmcDemand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcSubmitDemandActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitOmcDemandResponse>({
            mutation: SUBMIT_OMC_DEMAND,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              submissionUuid: request.uuid,
              isDeclared: request.isDeclared,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.submitOmcDemand;
              if (!payload) {
                return omcSubmitDemandActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? '',
                    data: null,
                  },
                });
              }
              return omcSubmitDemandActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcSubmitDemandActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteOmcDemandDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcDeleteDemandDraftActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteOmcDemandDraftResponse>({
            mutation: DELETE_OMC_DEMAND_DRAFT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              submissionUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.deleteOmcDemandDraft;
              if (!payload) {
                return omcDeleteDemandDraftActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? '',
                    data: null,
                  },
                });
              }

              return omcDeleteDemandDraftActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcDeleteDemandDraftActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadRequiredGuaranteeSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcRequiredGuaranteeSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<RequiredGuaranteeSummaryResponse>({
            query: REQURIRED_GUARANTEE_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              demandUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.requiredGuaranteeSummary;
              if (!payload) {
                return omcRequiredGuaranteeSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcRequiredGuaranteeSummaryActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcRequiredGuaranteeSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadRequiredOmcProductMarketShareSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcProductMarketShareSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<RequiredOmcProductMarketShareSummaryResponse>({
            query: REQUIRED_OMC_PRODUCT_MARKET_SHARE_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              demandUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.requiredOmcProductMarketShareSummary;
              if (!payload) {
                return omcProductMarketShareSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcProductMarketShareSummaryActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcProductMarketShareSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOverduePendingBillsSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcOverduePendingBillsSummaryActions.request),
      switchMap(() =>
        this.graphQlService
          .query<OverduePendingBillsSummaryResponse>({
            query: GET_OVERDUE_PENDING_BILL_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.overduePendingBillsSummary;
              if (!payload) {
                return omcOverduePendingBillsSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcOverduePendingBillsSummaryActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcOverduePendingBillsSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcTerminalAgreements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcTerminalAgreementsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcTerminalAgreementsResponse>({
            query: GET_ALL_OMC_TERMINAL_AGREEMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              omcUuid: request.omcUuid,
              pageable: request.pageable,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getOmcTerminalAgreementsByOmc;
              if (!payload) {
                return omcTerminalAgreementsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }

              return omcTerminalAgreementsActions.success({
                response: {
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                omcTerminalAgreementsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcTerminalAgreementByUuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcTerminalAgreementByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcTerminalAgreementResponse>({
            query: GET_OMC_TERMINAL_AGREEMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getOmcTerminalAgreement;
              if (!payload) {
                return omcTerminalAgreementByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to load terminal agreement.',
                    data: null,
                  },
                });
              }

              return omcTerminalAgreementByUuidActions.success({
                response: {
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                omcTerminalAgreementByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcStockReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcStockReportsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcStockReportsByOmcResponse>({
            query: GET_OMC_STOCK_REPORT_BY_OMC,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              omcUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getOmcStockReportsByOmc;
              if (!payload) {
                return omcStockReportsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to load OMC stock reports.',
                    data: null,
                  },
                });
              }

              return omcStockReportsActions.success({
                response: {
                  data: payload.data ?? [],
                },
              });
            }),
            catchError((error) =>
              of(
                omcStockReportsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcStockReportDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcStockReportDetailActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcStockReportDetailResponse>({
            query: GET_OMC_STOCK_REPORT_DETAIL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              uuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getOmcStockReport;
              if (!payload) {
                return omcStockReportDetailActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to load OMC stock report detail.',
                    data: null,
                  },
                });
              }

              if (payload.status === false) {
                return omcStockReportDetailActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to load OMC stock report detail.',
                    data: null,
                  },
                });
              }

              return omcStockReportDetailActions.success({
                response: {
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                omcStockReportDetailActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  createOmcStockReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcCreateStockReportActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<CreateOmcStockReportResponse>({
            mutation: CREATE_OMC_STOCK_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.createOmcStockReport;
              if (!payload || payload.status === false) {
                return omcCreateStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to create OMC stock report.',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcCreateStockReportActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcCreateStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateOmcStockReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcUpdateStockReportActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<UpdateOmcStockReportResponse>({
            mutation: UPDATE_OMC_STOCK_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.updateOmcStockReport;
              if (!payload || payload.status === false) {
                return omcUpdateStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to update OMC stock report.',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcUpdateStockReportActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcUpdateStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitOmcStockReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcSubmitStockReportActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitOmcStockReportResponse>({
            mutation: SUBMIT_OMC_STOCK_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              omcReportUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.submitOmcStockReport;
              if (!payload) {
                return omcSubmitStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? 'Failed to submit OMC stock report.',
                    data: null,
                  },
                });
              }

              if (payload.status === false) {
                return omcSubmitStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to submit OMC stock report.',
                    data: null,
                  },
                });
              }

              return omcSubmitStockReportActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcSubmitStockReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitOmcUsageReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcSubmitUsageReportActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitOmcUsageReportResponse>({
            mutation: SUBMIT_OMC_USAGE_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.submitOmcUsage;
              if (!payload) {
                return omcSubmitUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? 'Failed to submit OMC usage report.',
                    data: null,
                  },
                });
              }

              if (payload.status === false) {
                return omcSubmitUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to submit OMC usage report.',
                    data: null,
                  },
                });
              }

              return omcSubmitUsageReportActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcSubmitUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateOmcUsageReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcUpdateUsageReportActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<UpdateOmcUsageReportResponse>({
            mutation: UPDATE_OMC_USAGE_REPORT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.updateOmcUsage;
              if (!payload) {
                return omcUpdateUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: response.error?.message ?? 'Failed to update OMC usage report.',
                    data: null,
                  },
                });
              }

              if (payload.status === false) {
                return omcUpdateUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to update OMC usage report.',
                    data: null,
                  },
                });
              }

              return omcUpdateUsageReportActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcUpdateUsageReportActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  createOmcTerminalAgreement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcCreateTerminalAgreementActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<CreateOmcTerminalAgreementResponse>({
            mutation: CREATE_OMC_TERMINAL_AGREEMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.createOmcTerminalAgreement;
              if (!payload || payload.status === false) {
                return omcCreateTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to create terminal agreement',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcCreateTerminalAgreementActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcCreateTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateOmcTerminalAgreement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcUpdateTerminalAgreementActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<UpdateOmcTerminalAgreementResponse>({
            mutation: UPDATE_OMC_TERMINAL_AGREEMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.updateOmcTerminalAgreement;
              if (!payload || payload.status === false) {
                return omcUpdateTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to update terminal agreement',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcUpdateTerminalAgreementActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcUpdateTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  submitOmcTerminalAgreement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcSubmitTerminalAgreementActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitOmcTerminalAgreementResponse>({
            mutation: SUBMIT_OMC_TERMINAL_AGREEMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.submitOmcTerminalAgreement;
              if (!payload || payload.status === false) {
                return omcSubmitTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to submit terminal agreement',
                    data: payload?.data ?? null,
                  },
                });
              }

              return omcSubmitTerminalAgreementActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcSubmitTerminalAgreementActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  checkOmcTerminalSameTin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcTerminalSameTinActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<CheckOmcTerminalSameTinResponse>({
            query: CHECK_OMC_TERMINAL_SAME_TIN,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.checkOmcTerminalSameTin;
              if (!payload) {
                return omcTerminalSameTinActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to verify OMC and terminal TIN information.',
                    data: null,
                  },
                });
              }

              return omcTerminalSameTinActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                omcTerminalSameTinActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  omcSignContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcSignContractActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SignOmcContractResponse>({
            mutation: SIGN_OMC_CONTRACT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.signOmcContract;
              if (!payload || payload.status === false) {
                return OmcSignContractActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ?? response.error?.message ?? 'Failed to sign OMC contract',
                    data: payload?.data ?? null,
                  },
                });
              }

              return OmcSignContractActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                OmcSignContractActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadOmcCertificatePreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(omcCertificatePreviewActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcCertificatePreviewResponse>({
            query: GET_OMC_CERTIFICATE_PREVIEW,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              omcUuid: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getCertificateByOmcUuid;
              if (!payload) {
                return omcCertificatePreviewActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to load OMC certificate preview.',
                    data: null,
                  },
                });
              }

              if (payload.status === false) {
                return omcCertificatePreviewActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to load OMC certificate preview.',
                    data: null,
                  },
                });
              }

              return omcCertificatePreviewActions.success({
                response: {
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                omcCertificatePreviewActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  getLoggedInOmcLastMonthsProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedInOmcLastMonthsProjectsActions.request),
      switchMap(() =>
        this.graphQlService
          .mutate<GetLoggedInOmcLastMonthDemandProjectionResponse>({
            mutation: GET_LOGGED_IN_OMC_LAST_MONTHS_PROJECTS,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getLoggedInOmcLastMonthDemandProjection;
              if (!payload) {
                return loggedInOmcLastMonthsProjectsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch last month projections',
                    data: null,
                  },
                });
              }
              if (payload.status === false) {
                return loggedInOmcLastMonthsProjectsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to fetch last month projections',
                    data: null,
                  },
                });
              }
              return loggedInOmcLastMonthsProjectsActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                loggedInOmcLastMonthsProjectsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $loadBpsCalendar = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBpsCalendarActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<any>({
            query: GET_PUBLIC_ATTACHMENTS,
            variables: {
              pageableParam: {
                first: 0,
                size: 10,
                searchFields: [
                  {
                    fieldName: 'attachmentType',
                    fieldValue: 'ANNUAL_IMPORTATION_CALENDAR',
                    searchType: 'Equals',
                    queryOperator: 'AND',
                  },
                  {
                    fieldName: 'status',
                    fieldValue: 'ACTIVE',
                    searchType: 'Equals',
                    queryOperator: 'AND',
                  },
                ],
                sortBy: 'createdAt',
                sortDirection: 'DESC',
              },
            },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.publicAttachments;
              if (!payload) {
                return loadBpsCalendarActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch BPS calendar',
                    data: null,
                  },
                });
              }
              return loadBpsCalendarActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                loadBpsCalendarActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteOmcDemandCargoProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOmcDemandCargoProductActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteOmcDemandCargoProductResponse>({
            mutation: DELETE_OMC_DEMAND_CARGO_PRODUCT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              vesselUuid: request.vesselUuid,
              productUuid: request.productUuid,
              portUuid: request.portUuid,
              submissionUuid: request.submissionUuid,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.deleteOmcDemandCargoProduct;
              if (!payload || payload.status === false) {
                return deleteOmcDemandCargoProductActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to delete cargo product',
                    data: payload?.data ?? null,
                  },
                });
              }

              return deleteOmcDemandCargoProductActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                deleteOmcDemandCargoProductActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteOmcDemandProductUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOmcDemandProductUsageActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteOmcDemandProductUsageResponse>({
            mutation: DELETE_OMC_DEMAND_PRODUCT_USAGE,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              vesselUuid: request.vesselUuid,
              productUuid: request.productUuid,
              portUuid: request.portUuid,
              usageUuid: request.usageUuid,
              submissionUuid: request.submissionUuid,
              terminalUuid: request.terminalUuid,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.deleteOmcDemandProductUsage;
              if (!payload || payload.status === false) {
                return deleteOmcDemandProductUsageActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      payload?.message ??
                      response.error?.message ??
                      'Failed to delete product usage',
                    data: payload?.data ?? null,
                  },
                });
              }

              return deleteOmcDemandProductUsageActions.success({
                response: payload,
              });
            }),
            catchError((error) =>
              of(
                deleteOmcDemandProductUsageActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
